import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFocusStore, type UserFocus } from '@/hooks/use-focus-store'
import { focusRouteAction } from '@/utils/focus-route-gate'
import { invalidateFocusChangeQueries } from '@/utils/focus-navigation'

interface FocusRouteGateProps {
  routeFocus: string
  userFocus: UserFocus
  onFocusChange: (focus: UserFocus) => void
  /** Shown while the focus switch is in flight. */
  pending: ReactNode
  /** Shown when the user can't use this route (usually a redirect to their focus home). */
  fallback: ReactNode
  children: ReactNode
}

/**
 * Renders a focus route. Opening it for a role the user holds but isn't focused on
 * (e.g. a platform page while focused on another role) switches focus first instead of
 * bouncing to the current focus home.
 */
export function FocusRouteGate({
  routeFocus,
  userFocus,
  onFocusChange,
  pending,
  fallback,
  children,
}: FocusRouteGateProps) {
  const { setFocus, roleSwitchingEnabled } = useFocusStore()
  const queryClient = useQueryClient()
  const enteredRef = useRef(false)
  const requestedRef = useRef(false)
  const [switchFailed, setSwitchFailed] = useState(false)

  const action = focusRouteAction({
    routeFocus,
    userFocus,
    roleSwitchingEnabled,
    entered: enteredRef.current,
    switchFailed,
  })
  if (action === 'render') enteredRef.current = true

  useEffect(() => {
    if (action !== 'switch' || requestedRef.current) return
    requestedRef.current = true
    setFocus(routeFocus)
      .then((data) => {
        if (!data || data.current_focus !== routeFocus) {
          setSwitchFailed(true)
          return
        }
        invalidateFocusChangeQueries(queryClient)
        onFocusChange({
          current_focus: data.current_focus,
          available_foci: data.available_foci ?? userFocus.available_foci,
          focus_confirmed: data.focus_confirmed ?? userFocus.focus_confirmed,
        })
      })
      .catch((error: unknown) => {
        console.error('FocusRouteGate: could not switch focus from URL:', error)
        setSwitchFailed(true)
      })
  }, [action, routeFocus, setFocus, queryClient, onFocusChange, userFocus])

  if (action === 'render') return <>{children}</>
  if (action === 'switch') return <>{pending}</>
  return <>{fallback}</>
}
